import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Filler } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { motion, AnimatePresence } from 'framer-motion';
import { MONTHS, extendPalette } from './constants';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement, Filler, ChartDataLabels);

function MonthlySummaries({ budgetData, summaryData, setSummaryData, refreshData }) {
  // Always the 1st of the month — setMonth() on the 29th–31st would overflow into the wrong month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const latestSummaryRequest = useRef(0);
  const [isCurrentMonth, setIsCurrentMonth] = useState(true);
  const [historicalDataState, setHistoricalData] = useState({});
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Category drill-down state
  const [drillCategory, setDrillCategory] = useState(null);
  const [drillExpenses, setDrillExpenses] = useState([]);
  const [isDrillLoading, setIsDrillLoading] = useState(false);

  const handleCategoryDrill = useCallback(async (category) => {
    const year = selectedMonth.getFullYear();
    const monthName = MONTHS[selectedMonth.getMonth()];
    setDrillCategory(category);
    setIsDrillLoading(true);
    setDrillExpenses([]);

    const now = new Date();
    const isCurrent = year === now.getFullYear() && selectedMonth.getMonth() === now.getMonth();

    try {
      if (isCurrent) {
        // Current month: filter from budgets (Google Sheets)
        const response = await axios.get('/api/budgets', { withCredentials: true });
        const filtered = (response.data || []).filter(e => e.category === category);
        setDrillExpenses(filtered.map(e => ({
          date: e.date, amount: e.amount, reason: e.reason,
          category: e.category, paymentType: e.paymentType, who: e.who, kid: e.kid
        })));
      } else {
        // Historical: query Neon via new endpoint
        const response = await axios.get(`/api/expenses-by-category?month=${monthName}&year=${year}&category=${encodeURIComponent(category)}`);
        setDrillExpenses(response.data.expenses || []);
      }
    } catch (err) {
      console.error('Error loading category expenses:', err.message);
      setDrillExpenses([]);
    } finally {
      setIsDrillLoading(false);
    }
  }, [selectedMonth]);

  const handleMonthChange = useCallback((date) => {
    console.log('DatePicker changed to:', date);
    setSelectedMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    const now = new Date();
    setIsCurrentMonth(date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth());
  }, []);

  const fetchCurrentMonth = useCallback(async (month) => {
    // Only the latest request may update state — a slow earlier response must not overwrite a newer month
    const requestId = ++latestSummaryRequest.current;
    try {
      console.log('Fetching month data for:', month);
      const response = await axios.get(`/api/summaries?month=${month}`);
      if (requestId !== latestSummaryRequest.current) return;
      console.log('Month data fetched successfully:', response.data);
      setSummaryData(response.data || {});
    } catch (err) {
      if (requestId !== latestSummaryRequest.current) return;
      console.error('Error fetching month data:', err.message);
      setSummaryData({});
    }
  }, [setSummaryData]);

  const fetchHistoricalData = useCallback(async () => {
    console.log('fetchHistoricalData called, current state:', Object.keys(historicalDataState).length);
    if (Object.keys(historicalDataState).length > 0) {
      console.log('Historical data already loaded, skipping fetch');
      return;
    }
    setIsLoadingHistorical(true);
    try {
      // Single API call replaces ~48 individual month requests
      const response = await axios.get('/api/all-summaries');
      console.log('All historical data loaded in one call:', Object.keys(response.data).length, 'months');
      setHistoricalData(response.data || {});
    } catch (err) {
      console.error('Error fetching historical data:', err.message);
      setHistoricalData({});
    } finally {
      setIsLoadingHistorical(false);
    }
  }, [historicalDataState]);

  useEffect(() => {
    const year = selectedMonth.getFullYear();
    const monthName = MONTHS[selectedMonth.getMonth()];
    const monthStr = `${year}-${monthName}`;
    fetchCurrentMonth(monthStr);
  }, [selectedMonth, fetchCurrentMonth]);

  useEffect(() => {
    if (Object.keys(summaryData).length > 0 && Object.keys(historicalDataState).length === 0) {
      fetchHistoricalData();
    }
  }, [summaryData, historicalDataState, fetchHistoricalData]);

  const chartDataCategories = useMemo(() => {
    const labels = Object.keys(summaryData.byCategory || {}).filter(key => summaryData.byCategory[key] > 0);
    const values = Object.values(summaryData.byCategory || {}).filter(val => val > 0);
    
    const glassmorphismColors = [
      '#8B5CF6', // Bright Purple - much more visible!
      '#3B82F6', // Bright Blue  
      '#10B981', // Bright Green
      '#F59E0B', // Bright Yellow
      '#EF4444', // Bright Red
      '#A855F7', // Bright Violet
      '#22C55E', // Bright Emerald
      '#FBBF24', // Bright Amber
      '#F43F5E', // Bright Rose
      '#6366F1', // Bright Indigo
      '#06B6D4', // Bright Cyan
      '#84CC16', // Bright Lime
      '#D946EF'  // Bright Fuchsia
    ];
    
    const borderColors = [
      '#7C3AED', // Dark Purple border
      '#2563EB', // Dark Blue border
      '#059669', // Dark Green border
      '#D97706', // Dark Yellow border
      '#DC2626', // Dark Red border
      '#9333EA', // Dark Violet border
      '#16A34A', // Dark Emerald border
      '#F59E0B', // Dark Amber border
      '#E11D48', // Dark Rose border
      '#4F46E5', // Dark Indigo border
      '#0891B2', // Dark Cyan border
      '#65A30D', // Dark Lime border
      '#C026D3'  // Dark Fuchsia border
    ];
    
    return {
      labels,
      datasets: [{
        data: values,
        backgroundColor: extendPalette(glassmorphismColors, labels.length),
        borderColor: extendPalette(borderColors, labels.length, { lightness: 40, alpha: 1 }),
        borderWidth: 5,
        hoverBackgroundColor: extendPalette(glassmorphismColors, labels.length),
        hoverBorderColor: '#FFFFFF',
        hoverBorderWidth: 8,
        hoverOffset: 10
      }]
    };
  }, [summaryData]);

  const incomeVsSpendingChart = useMemo(() => ({
    labels: ['Spending', 'Income'],
    datasets: [{
      data: [summaryData.total || 0, summaryData.income || 0],
      backgroundColor: [
        '#EF4444', // BRIGHT Red for Spending
        '#10B981'  // BRIGHT Green for Income
      ],
      borderColor: [
        '#DC2626',
        '#059669'
      ],
      borderWidth: 5,
      borderRadius: 15,
      borderSkipped: false,
      hoverBackgroundColor: [
        '#F87171', // Even brighter red on hover
        '#34D399'  // Even brighter green on hover
      ],
      hoverBorderColor: '#FFFFFF',
      hoverBorderWidth: 8
    }]
  }), [summaryData]);

  const historicalTrendChart = useMemo(() => ({
    labels: Object.keys(historicalDataState).map(key => key),
    datasets: [
      {
        label: 'Spending',
        data: Object.values(historicalDataState).map(data => data.total || 0),
        borderColor: 'rgba(239, 68, 68, 1)',
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(239, 68, 68, 0.4)');
          gradient.addColorStop(0.5, 'rgba(239, 68, 68, 0.2)');
          gradient.addColorStop(1, 'rgba(239, 68, 68, 0.05)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(239, 68, 68, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgba(239, 68, 68, 1)',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3
      },
      {
        label: 'Income',
        data: Object.values(historicalDataState).map(data => data.income || 0),
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.4)');
          gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.2)');
          gradient.addColorStop(1, 'rgba(16, 185, 129, 0.05)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgba(16, 185, 129, 1)',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3
      }
    ]
  }), [historicalDataState]);

  const spendingVelocity = useMemo(() => {
    const now = new Date();
    const isCurrentMonthSelected = selectedMonth.getFullYear() === now.getFullYear() && selectedMonth.getMonth() === now.getMonth();
    const daysElapsed = isCurrentMonthSelected
      ? now.getDate()
      : new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
    const dailyAvg = summaryData.total / daysElapsed || 0;
    return { dailyAvg };
  }, [summaryData, selectedMonth]);

  const categoryTrendsChart = useMemo(() => {
    const topCats = Object.entries(summaryData.byCategory || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat]) => cat);
    const sixMonthsAgo = new Date(selectedMonth);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
    const labels = [];
    const datasets = topCats.map((cat, index) => ({
      label: cat,
      data: [],
      borderColor: ['rgba(239, 68, 68, 1)', 'rgba(16, 185, 129, 1)', 'rgba(59, 130, 246, 1)'][index],
      backgroundColor: ['rgba(239, 68, 68, 0.2)', 'rgba(16, 185, 129, 0.2)', 'rgba(59, 130, 246, 0.2)'][index],
      fill: false,
      tension: 0.4,
      pointRadius: 4,
      pointBackgroundColor: ['rgba(239, 68, 68, 1)', 'rgba(16, 185, 129, 1)', 'rgba(59, 130, 246, 1)'][index],
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: ['rgba(239, 68, 68, 1)', 'rgba(16, 185, 129, 1)', 'rgba(59, 130, 246, 1)'][index],
      pointHoverBorderColor: '#ffffff',
      pointHoverBorderWidth: 3
    }));
    for (let i = 0; i < 6; i++) {
      const date = new Date(sixMonthsAgo);
      date.setMonth(date.getMonth() + i);
      const key = `${date.getFullYear()}-${MONTHS[date.getMonth()]}`;
      labels.push(key.slice(-3));
      topCats.forEach((cat, idx) => {
        datasets[idx].data.push(historicalDataState[key]?.byCategory?.[cat] || 0);
      });
    }
    return { labels, datasets };
  }, [summaryData, historicalDataState, selectedMonth]);

  const profitLoss = summaryData.income !== undefined && summaryData.total !== undefined
    ? summaryData.income - summaryData.total
    : 0;
  const savingsRate = summaryData.income && summaryData.income > 0
    ? ((summaryData.income - summaryData.total) / summaryData.income * 100).toFixed(1)
    : 0;

  const prevMonth = useMemo(() => {
    const prev = new Date(selectedMonth);
    prev.setMonth(prev.getMonth() - 1);
    const year = prev.getFullYear();
    const monthName = MONTHS[prev.getMonth()];
    const key = `${year}-${monthName}`;
    return historicalDataState[key] || { total: 0, income: 0 };
  }, [selectedMonth, historicalDataState]);

  const spendingChange = prevMonth.total > 0
    ? ((summaryData.total - prevMonth.total) / prevMonth.total * 100).toFixed(1)
    : 0;
  const incomeChange = prevMonth.income > 0
    ? ((summaryData.income - prevMonth.income) / prevMonth.income * 100).toFixed(1)
    : 0;

  const topCategories = useMemo(() => {
    const sorted = Object.entries(summaryData.byCategory || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    const yearlyAvg = Object.keys(historicalDataState).reduce((acc, key) => {
      Object.entries(historicalDataState[key].byCategory || {}).forEach(([cat, val]) => {
        acc[cat] = (acc[cat] || 0) + val;
      });
      return acc;
    }, {});
    const monthCount = Object.keys(historicalDataState).length;
    return sorted.map(([cat, val]) => ({
      category: cat,
      amount: val,
      avg: (yearlyAvg[cat] || 0) / monthCount
    }));
  }, [summaryData, historicalDataState]);

  // Enhanced chart options with glassmorphism styling
  const glassmorphismChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 12,
            weight: 500
          },
          boxWidth: 15,
          padding: 15,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 13,
          weight: 600
        },
        bodyFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 12
        },
        displayColors: true,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ₪${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          tickBorderDash: [2, 4]
        },
        border: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 11
          },
          padding: 8,
          callback: (value) => `₪${value.toLocaleString('en-US')}`
        }
      },
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 11
          },
          padding: 8
        }
      }
    },
    animation: {
      duration: 1200,
      easing: 'easeOutQuart'
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          font: {
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            size: 12,
            weight: 500
          },
          boxWidth: 15,
          padding: 15,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        titleFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 13,
          weight: 600
        },
        bodyFont: {
          family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          size: 12
        },
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const percentage = ((value / summaryData.total) * 100).toFixed(1);
            return `${label}: ₪${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: false
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeOutQuart'
    }
  };

  console.log('MonthlySummaries rendering with summaryData:', summaryData);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i);

  const handleMonthSelect = (year, monthIndex) => {
    const newDate = new Date(year, monthIndex, 1);
    handleMonthChange(newDate);
    setShowMonthPicker(false);
  };

  const toggleMonthPicker = () => {
    if (!showMonthPicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPickerPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
    }
    setShowMonthPicker(!showMonthPicker);
  };

  return (
    <motion.div
      className="min-h-screen p-4 md:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full max-w-6xl mx-auto">
        <header className="mb-8 glass glass-hover rounded-2xl p-6 text-center animate-slideInLeft">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Monthly Budget Summary</h1>
          <p className="text-white/70 mb-6">Track and analyze your monthly financial performance</p>

          {/* Custom Premium Month Picker */}
          <div className="relative inline-block">
            <button
              ref={buttonRef}
              onClick={toggleMonthPicker}
              className="glass-button px-6 py-3 rounded-xl text-white font-semibold text-lg flex items-center space-x-3 min-w-[240px] justify-between"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{`${MONTHS[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`}</span>
              <svg className={`w-5 h-5 transition-transform duration-300 ${showMonthPicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Month Picker Portal */}
          {typeof window !== 'undefined' && ReactDOM.createPortal(
            <AnimatePresence>
              {showMonthPicker && (
                <>
                  {/* Backdrop */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
                    onClick={() => setShowMonthPicker(false)}
                  />

                  {/* Month Picker Dropdown */}
                  <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={{
                      position: 'fixed',
                      top: `${pickerPosition.top}px`,
                      left: `${pickerPosition.left}px`,
                      transform: 'translateX(-50%)',
                      zIndex: 9999
                    }}
                    className="w-[420px] max-w-[95vw]"
                  >
                    <div className="glass rounded-2xl p-6 border-2 border-white/30 shadow-2xl">
                      <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                        {years.slice().reverse().map(year => (
                          <div key={year} className="mb-6 last:mb-0">
                            <h3 className="text-white/90 font-bold text-lg mb-3 pb-2 border-b border-white/20">
                              {year}
                            </h3>
                            <div className="grid grid-cols-3 gap-2">
                              {months.map((month, idx) => {
                                const isSelected = selectedMonth.getFullYear() === year && selectedMonth.getMonth() === idx;
                                const isFuture = year > currentYear || (year === currentYear && idx > new Date().getMonth());
                                const isDisabled = year < 2020 || isFuture;

                                return (
                                  <button
                                    key={month}
                                    disabled={isDisabled}
                                    onClick={() => !isDisabled && handleMonthSelect(year, idx)}
                                    className={`
                                      px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                      ${isSelected
                                        ? 'bg-gradient-to-br from-purple-500/80 to-purple-600/80 text-white shadow-lg ring-2 ring-white/40 scale-105'
                                        : isDisabled
                                          ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                          : 'bg-white/10 text-white/90 hover:bg-white/20 hover:scale-105 hover:shadow-md'
                                      }
                                    `}
                                  >
                                    {month.slice(0, 3)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>,
            document.body
          )}
        </header>
        {summaryData && Object.keys(summaryData).length > 0 ? (
          <div className="space-y-6">
            {/* Key Financial Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-1 bg-green-500/60 backdrop-blur-sm"></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Income</h3>
                  <p className="text-2xl font-bold text-white">
                    ₪{(summaryData.income || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-white/80 mt-1">Monthly income</p>
                </div>
              </div>
              <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-1 bg-red-500/60 backdrop-blur-sm"></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Spending</h3>
                  <p className="text-2xl font-bold text-white">
                    ₪{(summaryData.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-white/80 mt-1">Total expenses</p>
                </div>
              </div>
              <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                <div className={`p-1 ${profitLoss >= 0 ? 'bg-green-500/60' : 'bg-red-500/60'} backdrop-blur-sm`}></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Net Savings</h3>
                  <p className={`text-2xl font-bold ${profitLoss >= 0 ? 'text-white' : 'text-red-300'}`}>
                    ₪{profitLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-white/80 mt-1">Income minus expenses</p>
                </div>
              </div>
              <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                <div className={`p-1 ${savingsRate >= 20 ? 'bg-green-500/60' : savingsRate >= 0 ? 'bg-yellow-500/60' : 'bg-red-500/60'} backdrop-blur-sm`}></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Savings Rate</h3>
                  <p className={`text-2xl font-bold ${savingsRate >= 20 ? 'text-white' : savingsRate >= 0 ? 'text-yellow-300' : 'text-red-300'}`}>
                    {savingsRate}%
                  </p>
                  <p className="text-xs text-white/80 mt-1">Percentage of income saved</p>
                </div>
              </div>
              <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                <div className="p-1 bg-indigo-500/60 backdrop-blur-sm"></div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Daily Average</h3>
                  <p className="text-2xl font-bold text-white">
                    ₪{spendingVelocity.dailyAvg.toFixed(2)}/day
                  </p>
                  <p className="text-xs text-white/80 mt-1">Spending velocity</p>
                </div>
              </div>
              {isCurrentMonth && (
                <>
                  <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                    <div className="p-1 bg-blue-500/60 backdrop-blur-sm"></div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Daniel's Total</h3>
                      <p className="text-2xl font-bold text-white">
                        ₪{(summaryData.danielsTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-white/80 mt-1">Daniel's monthly spending</p>
                    </div>
                  </div>
                  <div className="glass glass-hover overflow-hidden transition-all duration-300 transform hover:-translate-y-1">
                    <div className="p-1 bg-purple-500/60 backdrop-blur-sm"></div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-1">Yehudit's Total</h3>
                      <p className="text-2xl font-bold text-white">
                        ₪{(summaryData.yehuditsTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-white/80 mt-1">Yehudit's monthly spending</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Month-over-Month Comparison */}
            <div className="glass glass-hover p-6 transition-all duration-300">
              <h3 className="text-lg font-semibold text-white mb-4">Month-over-Month Comparison</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center p-4 bg-indigo-950/30 rounded-lg backdrop-blur-sm">
                  <div className={`mr-4 p-3 rounded-full ${spendingChange >= 0 ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      {spendingChange >= 0 ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"></path>
                      )}
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-white/70">Spending Change</p>
                    <p className={`text-xl font-bold ${spendingChange >= 0 ? 'text-red-300' : 'text-green-300'}`}>
                      {spendingChange >= 0 ? '+' : ''}{spendingChange}%
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-indigo-950/30 rounded-lg backdrop-blur-sm">
                  <div className={`mr-4 p-3 rounded-full ${incomeChange >= 0 ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      {incomeChange >= 0 ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"></path>
                      )}
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-white/70">Income Change</p>
                    <p className={`text-xl font-bold ${incomeChange >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                      {incomeChange >= 0 ? '+' : ''}{incomeChange}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Income vs Spending & Velocity/Gauge */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="glass glass-hover p-6 transition-all duration-300">
                <h3 className="text-lg font-semibold text-white mb-4">Income vs Spending</h3>
                <motion.div
                  className="chart-container"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <Bar
                    data={incomeVsSpendingChart}
                    options={{
                      ...glassmorphismChartOptions,
                      plugins: {
                        ...glassmorphismChartOptions.plugins,
                        legend: { display: false }
                      }
                    }}
                    height={220}
                  />
                </motion.div>
              </div>
              <div className="glass glass-hover p-6 transition-all duration-300">
                <h3 className="text-lg font-semibold text-white mb-4">Spending Velocity</h3>
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="relative w-48 h-48">
                    <div className="absolute inset-0 flex items-center justify-center flex-col">
                      <p className="text-3xl font-bold text-white">₪{spendingVelocity.dailyAvg.toFixed(0)}</p>
                      <p className="text-sm text-white/70">per day</p>
                    </div>
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="10" />
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="rgba(255,255,255,0.8)"
                        strokeWidth="10"
                        strokeDasharray="283"
                        strokeDashoffset={283 * (1 - spendingVelocity.dailyAvg / (summaryData.income / 30 || 1))}
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                  </div>
                  <p className="mt-4 text-sm text-white/80">Based on {new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate()} days</p>
                </div>
              </div>
            </motion.div>

            {/* Spending Categories */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="glass glass-hover p-6 transition-all duration-300">
                <h3 className="text-lg font-semibold text-white mb-4">Spending by Category</h3>
                {chartDataCategories.labels.length > 0 ? (
                  <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, rotate: -10 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    <Pie
                      data={chartDataCategories}
                      options={pieChartOptions}
                    />
                  </motion.div>
                ) : (
                  <div className="flex justify-center items-center h-48">
                    <p className="text-white/70 text-center">No spending data available</p>
                  </div>
                )}
              </div>
              <div className="glass glass-hover p-6 transition-all duration-300">
                <h3 className="text-lg font-semibold text-white mb-4">Top Category Trends (6 Months)</h3>
                {isLoadingHistorical ? (
                  <div className="flex justify-center items-center h-48">
                    <div className="animate-pulse flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full border-4 border-white/50 border-t-transparent animate-spin mb-4"></div>
                      <p className="text-white/70">Loading trends...</p>
                    </div>
                  </div>
                ) : categoryTrendsChart.datasets.length > 0 ? (
                  <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    <Line
                      data={categoryTrendsChart}
                      options={{
                        ...glassmorphismChartOptions,
                        plugins: {
                          ...glassmorphismChartOptions.plugins,
                          legend: {
                            position: 'top',
                            labels: {
                              color: 'rgba(255, 255, 255, 0.9)',
                              font: {
                                family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                size: 12,
                                weight: 500
                              },
                              boxWidth: 12,
                              usePointStyle: true,
                              padding: 15
                            }
                          }
                        },
                        scales: {
                          ...glassmorphismChartOptions.scales,
                          y: {
                            ...glassmorphismChartOptions.scales.y,
                            ticks: {
                              ...glassmorphismChartOptions.scales.y.ticks,
                              callback: (value) => `₪${value.toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' })}`
                            }
                          }
                        }
                      }}
                    />
                  </motion.div>
                ) : (
                  <div className="flex justify-center items-center h-48">
                    <p className="text-white/70 text-center">No trend data available</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Categories Breakdown — click any category to see individual expenses */}
            <div className="glass glass-hover p-6 transition-all duration-300">
              <h3 className="text-lg font-semibold text-white mb-1">Categories Breakdown</h3>
              <p className="text-sm text-white/70 mb-4">Click a category to see individual expenses</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(summaryData.byCategory || {})
                  .filter(([_, amount]) => amount > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, amount]) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryDrill(category)}
                      className="bg-indigo-950/30 rounded-lg p-4 backdrop-blur-sm text-left transition-all duration-200 hover:bg-white/20 hover:scale-[1.02] cursor-pointer group"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-white group-hover:text-white">{category}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">
                            ₪{amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <svg className="w-4 h-4 text-white/40 group-hover:text-white/80 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div
                          className="bg-white/80 h-2 rounded-full"
                          style={{ width: `${Math.min(100, (amount / (summaryData.total || 1)) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-white/80 mt-1">
                        {((amount / (summaryData.total || 1)) * 100).toFixed(1)}% of total spending
                      </p>
                    </button>
                  ))}
              </div>
            </div>

            {/* Category Drill-Down Modal */}
            {drillCategory && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={() => setDrillCategory(null)}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className="glass w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center p-6 border-b border-white/20">
                    <div>
                      <h2 className="text-xl font-bold text-white">{drillCategory}</h2>
                      <p className="text-sm text-white/80">
                        {`${MONTHS[selectedMonth.getMonth()]} ${selectedMonth.getFullYear()}`}
                        {drillExpenses.length > 0 && ` — ${drillExpenses.length} expenses, ₪${drillExpenses.reduce((s, e) => s + (e.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </p>
                    </div>
                    <button onClick={() => setDrillCategory(null)} className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(85vh-100px)] p-6">
                    {isDrillLoading ? (
                      <div className="flex justify-center items-center py-12">
                        <div className="w-10 h-10 rounded-full border-4 border-white/50 border-t-transparent animate-spin"></div>
                      </div>
                    ) : drillExpenses.length === 0 ? (
                      <p className="text-white/80 text-center py-12">No individual expenses found for this category.</p>
                    ) : (
                      <>
                        {/* Desktop table */}
                        <div className="hidden md:block">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-white/20">
                                <th className="py-3 px-3 text-left text-white/70 font-medium">Date</th>
                                <th className="py-3 px-3 text-right text-white/70 font-medium">Amount</th>
                                <th className="py-3 px-3 text-left text-white/70 font-medium">Reason</th>
                                <th className="py-3 px-3 text-left text-white/70 font-medium">Payment</th>
                                <th className="py-3 px-3 text-left text-white/70 font-medium">Who</th>
                              </tr>
                            </thead>
                            <tbody>
                              {drillExpenses.map((exp, idx) => (
                                <tr key={idx} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                                  <td className="py-3 px-3 text-white/90 whitespace-nowrap">{exp.date}</td>
                                  <td className="py-3 px-3 text-white/90 text-right font-medium">₪{exp.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="py-3 px-3 text-white/90">{exp.reason}</td>
                                  <td className="py-3 px-3 text-white/70">{exp.paymentType}</td>
                                  <td className="py-3 px-3 text-white/70">{exp.who}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Mobile cards */}
                        <div className="md:hidden space-y-3">
                          {drillExpenses.map((exp, idx) => (
                            <div key={idx} className="bg-white/10 rounded-lg p-4">
                              <div className="flex justify-between items-start mb-1">
                                <span className="text-white font-medium text-sm">{exp.reason}</span>
                                <span className="text-white font-semibold">₪{exp.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                              </div>
                              <p className="text-xs text-white/80">{exp.date} {exp.who ? `- ${exp.who}` : ''} {exp.paymentType ? `- ${exp.paymentType}` : ''}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              </div>
            )}

            {/* Person Breakdown */}
            <div className="glass glass-hover p-6 transition-all duration-300">
              {isCurrentMonth ? (
                <>
                  <h3 className="text-lg font-semibold text-white mb-4">Daniel vs Yehudit</h3>
                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-white/80">Daniel</span>
                        <span className="text-sm font-medium text-white">₪{(summaryData.danielsTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2.5">
                        <div
                          className="bg-blue-400 h-2.5 rounded-full"
                          style={{ width: `${Math.min(100, ((summaryData.danielsTotal || 0) / (summaryData.total || 1)) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-white/80 mt-1">
                        {((summaryData.danielsTotal || 0) / (summaryData.total || 1) * 100).toFixed(1)}% of total spending
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-white/80">Yehudit</span>
                        <span className="text-sm font-medium text-white">₪{(summaryData.yehuditsTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2.5">
                        <div
                          className="bg-purple-400 h-2.5 rounded-full"
                          style={{ width: `${Math.min(100, ((summaryData.yehuditsTotal || 0) / (summaryData.total || 1)) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-white/80 mt-1">
                        {((summaryData.yehuditsTotal || 0) / (summaryData.total || 1) * 100).toFixed(1)}% of total spending
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-white mb-4">Top Categories vs Yearly Avg</h3>
                  {isLoadingHistorical ? (
                    <div className="flex justify-center items-center h-48">
                      <div className="animate-pulse flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full border-4 border-white/50 border-t-transparent animate-spin mb-4"></div>
                        <p className="text-white/70">Loading averages...</p>
                      </div>
                    </div>
                  ) : (
                    <table className="w-full text-sm text-white/80">
                      <thead>
                        <tr className="border-b border-white/20">
                          <th className="py-2 text-left text-white">Category</th>
                          <th className="py-2 text-right text-white">This Month</th>
                          <th className="py-2 text-right text-white">Yearly Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCategories.map(({ category, amount, avg }) => (
                          <tr key={category} className="border-b border-white/10">
                            <td className="py-1 text-white/90">{category}</td>
                            <td className="py-1 text-right text-white/90">₪{amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-1 text-right text-white/90">₪{avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>

            {/* Kids Accounts */}
            {isCurrentMonth && summaryData.kidsRunningTotal && Object.keys(summaryData.kidsRunningTotal).length > 0 && (
              <div className="glass glass-hover p-6 transition-all duration-300">
                <h3 className="text-lg font-semibold text-white mb-4">Kids Accounts Overview</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(summaryData.kidsRunningTotal || {}).map(([kid, balance]) => (
                    <div key={kid} className="bg-indigo-950/30 rounded-lg p-4 backdrop-blur-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-white">{kid}</span>
                        <span className={`font-medium ${balance >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          ₪{balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2">
                        <div
                          className={`${balance >= 0 ? 'bg-green-400' : 'bg-red-400'} h-2 rounded-full`}
                          style={{ width: `${Math.min(100, Math.abs(balance) / 1000 * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-white/80 mt-1">Running balance</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Historical Trends */}
            <motion.div 
              className="glass glass-hover p-6 transition-all duration-300"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <h3 className="text-lg font-semibold text-white mb-4 text-center">Historical Trends (All Data)</h3>
              {isLoadingHistorical ? (
                <p className="text-white/70 text-center">Loading trends...</p>
              ) : (
                <motion.div
                  className="chart-container"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  <Line
                    data={historicalTrendChart}
                    options={{
                      ...glassmorphismChartOptions,
                      plugins: {
                        ...glassmorphismChartOptions.plugins,
                        legend: {
                          position: 'top',
                          labels: {
                            color: 'rgba(255, 255, 255, 0.9)',
                            font: {
                              family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              size: 12,
                              weight: 500
                            },
                            boxWidth: 12,
                            usePointStyle: true,
                            padding: 15
                          }
                        }
                      },
                      scales: {
                        ...glassmorphismChartOptions.scales,
                        y: {
                          ...glassmorphismChartOptions.scales.y,
                          title: {
                            display: true,
                            text: 'Amount (₪)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            font: {
                              family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              size: 12,
                              weight: 600
                            }
                          }
                        }
                      }
                    }}
                  />
                </motion.div>
              )}
            </motion.div>
          </div>
        ) : (
          <div className="glass glass-hover p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-white/70 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
            </svg>
            <h3 className="text-xl font-medium text-white mb-2">No Data Available</h3>
            <p className="text-white/70 mb-4">No financial data is available for the selected month.</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default MonthlySummaries;